import React, { useEffect, useRef, useState } from "react";
import { Chart, ChartConfiguration, registerables } from "chart.js";

// Register Chart.js components
Chart.register(...registerables);

interface WebViewProps {
  style?: React.CSSProperties;
  reportUrl?: string;
  reportName?: string;
}

const WebView: React.FC<WebViewProps> = ({ style, reportUrl, reportName }) => {
  const revenueProfitChartRef = useRef<HTMLCanvasElement>(null);
  const expenseChartRef = useRef<HTMLCanvasElement>(null);
  const pettyCashChartRef = useRef<HTMLCanvasElement>(null);
  const serviceChartRef = useRef<HTMLCanvasElement>(null);

  // Chart instances refs for cleanup
  const revenueProfitChartInstance = useRef<Chart | null>(null);
  const expenseChartInstance = useRef<Chart | null>(null);
  const pettyCashChartInstance = useRef<Chart | null>(null);
  const serviceChartInstance = useRef<Chart | null>(null);

  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    // Cleanup function to destroy existing charts
    const cleanupCharts = () => {
      if (revenueProfitChartInstance.current) {
        revenueProfitChartInstance.current.destroy();
        revenueProfitChartInstance.current = null;
      }
      if (expenseChartInstance.current) {
        expenseChartInstance.current.destroy();
        expenseChartInstance.current = null;
      }
      if (pettyCashChartInstance.current) {
        pettyCashChartInstance.current.destroy();
        pettyCashChartInstance.current = null;
      }
      if (serviceChartInstance.current) {
        serviceChartInstance.current.destroy();
        serviceChartInstance.current = null;
      }
    };

    // Clean up existing charts before creating new ones
    cleanupCharts();

    // CSS Custom Properties
    const rootStyles = getComputedStyle(document.documentElement);
    const primary = "#22c55e";
    const primaryLight = "#5DCC5A";
    const secondary = "#3b82f6";
    const gray200 = "#e5e7eb";
    const gray500 = "#6b7280";
    const gray700 = "#374151";

    // Revenue & Profit Chart (only for overview tab)
    if (activeTab === "overview" && revenueProfitChartRef.current) {
      const ctx = revenueProfitChartRef.current.getContext("2d");
      if (ctx) {
        revenueProfitChartInstance.current = new Chart(ctx, {
          type: "line",
          data: {
            labels: [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ],
            datasets: [
              {
                label: "Revenue",
                data: [
                  50000, 52000, 60000, 65000, 70000, 75000, 80000, 78000, 82000,
                  85000, 90000, 95000,
                ],
                backgroundColor: secondary + "33",
                borderColor: secondary,
                fill: true,
                tension: 0.4,
              },
              {
                label: "Profit",
                data: [
                  12000, 14000, 16000, 18000, 20000, 22000, 24000, 23000, 25000,
                  27000, 29000, 31000,
                ],
                backgroundColor: primary + "33",
                borderColor: primary,
                fill: true,
                tension: 0.4,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                ticks: { font: { size: 12 }, color: gray700 },
              },
              x: {
                ticks: { font: { size: 12 }, color: gray700 },
              },
            },
            plugins: {
              legend: {
                position: "top",
                labels: { font: { size: 14 }, color: gray700 },
              },
            },
          },
        });
      }
    }

    // Expense Chart (only for financial tab)
    if (activeTab === "financial" && expenseChartRef.current) {
      const ctx = expenseChartRef.current.getContext("2d");
      if (ctx) {
        expenseChartInstance.current = new Chart(ctx, {
          type: "pie",
          data: {
            labels: ["Labour", "Parts", "Utilities"],
            datasets: [
              {
                data: [45, 30, 25],
                backgroundColor: [primary, primaryLight, gray500],
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
          },
        });
      }
    }

    // Petty Cash Chart (only for financial tab)
    if (activeTab === "financial" && pettyCashChartRef.current) {
      const ctx = pettyCashChartRef.current.getContext("2d");
      if (ctx) {
        pettyCashChartInstance.current = new Chart(ctx, {
          type: "pie",
          data: {
            labels: ["Emergency Parts", "Courier", "Misc"],
            datasets: [
              {
                data: [40, 30, 30],
                backgroundColor: [primary, primaryLight, gray500],
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
          },
        });
      }
    }

    // Service Performance Chart (only for services tab)
    if (activeTab === "services" && serviceChartRef.current) {
      const ctx = serviceChartRef.current.getContext("2d");
      if (ctx) {
        serviceChartInstance.current = new Chart(ctx, {
          type: "bar",
          data: {
            labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
            datasets: [
              {
                label: "Pending",
                data: [25, 20, 30, 15, 10, 5, 20],
                backgroundColor: gray200,
                barThickness: 18,
              },
              {
                label: "Completed",
                data: [75, 80, 70, 85, 90, 95, 80],
                backgroundColor: primary,
                barThickness: 18,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                max: 100,
                ticks: { stepSize: 25, font: { size: 12 }, color: gray700 },
              },
              x: { ticks: { font: { size: 12 }, color: gray700 } },
            },
            plugins: {
              legend: { display: false },
            },
          },
        });
      }
    }

    // Return cleanup function
    return cleanupCharts;
  }, [activeTab]);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    // Focus first searchable dropdown input in the new tab after a short delay
    setTimeout(() => {
      const firstSearchableDropdownInput = document.querySelector('[data-searchable-dropdown-input="true"]:not([disabled])') as HTMLInputElement;
      if (firstSearchableDropdownInput) {
        firstSearchableDropdownInput.focus();
      }
    }, 100);
  };

  // If reportUrl is provided, render iframe placeholder
  if (reportUrl) {
    return (
      <div
        className="font-sans text-sm min-h-screen bg-white dark:bg-gray-900 p-6 transition-colors duration-200"
        style={style}
      >
        <div className="bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-lg py-12 px-6 text-center">
          <div className="text-5xl mb-4">📊</div>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-2">
            Report: {reportName || "Dashboard Report"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Report URL: {reportUrl}
          </p>
          <div className="bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-md py-3 px-4 text-blue-800 dark:text-blue-300 text-[13px] max-w-[600px] mx-auto">
            <strong>Note:</strong> In production, this would render an iframe or
            embedded report from the specified URL. The report would be loaded
            dynamically based on the reportUrl and reportName configuration.
          </div>
        </div>
      </div>
    );
  }

  // Default dashboard rendering when no reportUrl is provided
  return (
    <div
      className="font-sans text-sm min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200"
      style={style}
    >
      <style>{`
        .max-h-65vh {
          max-height: 65vh;
          overflow-y: auto;
        }
        .tab-btn {
          border: none;
          background: transparent;
          padding: 0.25rem 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 0.375rem 0.375rem 0 0;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tab-btn.active {
          background-color: #f3f4f6;
          border-bottom: 2px solid #22c55e;
          color: #22c55e;
        }
        .table-header-primary th {
          background-color: #22c55e;
          color: white;
        }
      `}</style>

      <div className="w-full">
        {/* Header & Date Filter */}
        <div className="flex py-2 mb-2 gap-2">
          <div className="flex-1"></div>
          <div className="ml-auto">
            <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm">
              <span className="text-gray-500 dark:text-gray-400 font-medium mr-1">
                Filter by date
              </span>
              <span className="bg-gray-100 dark:bg-gray-700 rounded-full py-1 px-2 text-gray-800 dark:text-gray-200 font-semibold">
                July 1st, 2025
              </span>
              <span className="text-gray-500 dark:text-gray-400 mx-1">to</span>
              <span className="bg-gray-100 dark:bg-gray-700 rounded-full py-1 px-2 text-gray-800 dark:text-gray-200 font-semibold">
                July 11th, 2025
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-300 sticky top-0 bg-white py-1 overflow-x-auto">
          <div className="flex">
            <button
              className={`tab-btn ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => handleTabClick("overview")}
            >
              Overview
            </button>
            <button
              className={`tab-btn ${activeTab === "financial" ? "active" : ""}`}
              onClick={() => handleTabClick("financial")}
            >
              Financial
            </button>
            <button
              className={`tab-btn ${activeTab === "team" ? "active" : ""}`}
              onClick={() => handleTabClick("team")}
            >
              Team
            </button>
            <button
              className={`tab-btn ${activeTab === "inventory" ? "active" : ""}`}
              onClick={() => handleTabClick("inventory")}
            >
              Inventory
            </button>
            <button
              className={`tab-btn ${activeTab === "services" ? "active" : ""}`}
              onClick={() => handleTabClick("services")}
            >
              Services
            </button>
          </div>
        </div>

        {/* Main Content Scroll Area */}
        <div style={{ flexGrow: 1 }}>
          {/* Overview Tab Content */}
          {activeTab === "overview" && (
            <div className="flex flex-column max-h-65vh p-3" style={{ gap: "0.75rem" }}>
              <div className="flex-1">
                <h2
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                  }}
                >
                  Revenue & Profit Analysis
                </h2>
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "0.75rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #e5e7eb",
                    boxShadow: "var(--shadow)",
                    width: "100%",
                  }}
                >
                  <canvas
                    ref={revenueProfitChartRef}
                    style={{ width: "100%", height: "12rem" }}
                  ></canvas>
                </div>
              </div>
              <div className="" style={{ gap: "0.5rem" }}>
                <div
                  className="d-flex align-items-center mb-1"
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    color: "var(--primary)",
                  }}
                >
                  Today's Critical Alerts
                </div>
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "0.75rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #e5e7eb",
                    boxShadow: "var(--shadow)",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#f3f4f6",
                      borderRadius: "0.375rem",
                      padding: "0.5rem",
                      marginBottom: "0.5rem",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#6b7280",
                        marginBottom: "0.25rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      Inventory Issues:
                    </div>
                    <ul
                      style={{
                        paddingLeft: "1rem",
                        color: "#6b7280",
                        fontSize: "0.875rem",
                        marginBottom: 0,
                      }}
                    >
                      <li>Battery stock critically low (8 units)</li>
                      <li>Air filters need restocking (5 units)</li>
                      <li>Shock absorbers below minimum (4 units)</li>
                    </ul>
                  </div>
                  <div
                    style={{
                      backgroundColor: "#f3f4f6",
                      borderRadius: "0.375rem",
                      padding: "0.5rem",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#6b7280",
                        marginBottom: "0.25rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      Operations:
                    </div>
                    <ul
                      style={{
                        paddingLeft: "1rem",
                        color: "#6b7280",
                        fontSize: "0.875rem",
                        marginBottom: 0,
                      }}
                    >
                      <li>Electricity costs up 15% this month</li>
                      <li>Omar has 5 active tickets</li>
                      <li>6 services pending completion</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Financial Tab Content */}
          {activeTab === "financial" && (
            <div className="row max-h-65vh p-3" style={{ gap: "0.75rem" }}>
              <div className="col-md-4">
                <h2
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                  }}
                >
                  Expense Breakdown
                </h2>
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #e5e7eb",
                    boxShadow: "var(--shadow)",
                  }}
                >
                  <canvas
                    ref={expenseChartRef}
                    style={{ width: "100%", height: "12rem" }}
                  ></canvas>
                </div>
              </div>
              <div className="col-md-4">
                <h2
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                  }}
                >
                  Petty Cash by Category
                </h2>
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #e5e7eb",
                    boxShadow: "var(--shadow)",
                  }}
                >
                  <canvas
                    ref={pettyCashChartRef}
                    style={{ width: "100%", height: "12rem" }}
                  ></canvas>
                </div>
              </div>
              <div className="col-md-4">
                <h2
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                  }}
                >
                  Top Revenue-Generating Services
                </h2>
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #e5e7eb",
                    boxShadow: "var(--shadow)",
                  }}
                >
                  <div style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Engine Tune-Up</span>
                      <span>40%</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Oil Change</span>
                      <span>20%</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Battery Replacement</span>
                      <span>18%</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>AC Repair</span>
                      <span>12%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Team Tab Content */}
          {activeTab === "team" && (
            <div className="row max-h-65vh p-3" style={{ gap: "0.75rem" }}>
              <div className="col-md-4">
                <div className="d-flex flex-column" style={{ gap: "0.75rem" }}>
                  {[
                    { label: "Total Tickets", value: "120" },
                    {
                      label: "Avg Rating (Customer Satisfaction)",
                      value: "4.2",
                    },
                    { label: "Open Job Cards", value: "15" },
                    { label: "Delayed Services", value: "12" },
                    { label: "Technician of the Month", value: "Arun S" },
                    { label: "Repeat Customers", value: "120" },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center justify-content-between border-left-primary"
                      style={{
                        backgroundColor: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        borderRadius: "0.75rem",
                        paddingLeft: "0.75rem",
                        paddingRight: "1.25rem",
                        paddingTop: "0.75rem",
                        paddingBottom: "0.75rem",
                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <span style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                        {item.label}
                      </span>
                      <span
                        style={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                          color: "#1f2937",
                        }}
                      >
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-md-8">
                <h2
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                  }}
                >
                  Team Performance
                </h2>
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #e5e7eb",
                    boxShadow: "var(--shadow)",
                    overflowX: "auto",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      fontSize: "0.875rem",
                      borderCollapse: "collapse",
                    }}
                  >
                    <thead className="table-header-primary">
                      <tr>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          User ID
                        </th>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          User Name
                        </th>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          Status
                        </th>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          Tickets Closed
                        </th>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          Efficiency
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          id: "ID-90909",
                          name: "Anandhu S",
                          status: "Active",
                          tickets: "Ernakulam via Aluva",
                          efficiency: "95%",
                          statusColor: "#22c55e",
                        },
                        {
                          id: "ID-90912",
                          name: "Kiran Kumar",
                          status: "Inactive",
                          tickets: "Perumbavoor via Muvattupuzha",
                          efficiency: "95%",
                          statusColor: "#ef4444",
                        },
                        {
                          id: "ID-90922",
                          name: "Vishnu S K",
                          status: "Active",
                          tickets: "Kollam, Kerala",
                          efficiency: "95%",
                          statusColor: "#22c55e",
                        },
                        {
                          id: "ID-90909",
                          name: "Anandhu S",
                          status: "Active",
                          tickets: "Palakkad via Vadakancheri",
                          efficiency: "75%",
                          statusColor: "#22c55e",
                        },
                        {
                          id: "ID-90922",
                          name: "Vishnu S K",
                          status: "Active",
                          tickets: "Kollam, Kerala",
                          efficiency: "95%",
                          statusColor: "#22c55e",
                        },
                      ].map((row, index) => (
                        <tr
                          key={index}
                          style={{
                            borderBottom:
                              index < 4 ? "1px solid #f3f4f6" : "none",
                          }}
                        >
                          <td style={{ padding: "0.5rem" }}>{row.id}</td>
                          <td style={{ padding: "0.5rem" }}>{row.name}</td>
                          <td
                            style={{
                              padding: "0.5rem",
                              color: row.statusColor,
                            }}
                          >
                            {row.status}
                          </td>
                          <td style={{ padding: "0.5rem" }}>{row.tickets}</td>
                          <td
                            style={{
                              padding: "0.5rem",
                              color:
                                row.efficiency === "75%"
                                  ? "#f59e42"
                                  : "#22c55e",
                            }}
                          >
                            {row.efficiency}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Inventory Tab Content */}
          {activeTab === "inventory" && (
            <div
              className="d-flex flex-column max-h-65vh p-3"
              style={{ gap: "0.75rem" }}
            >
              <div style={{ width: "100%" }}>
                <h2
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                  }}
                >
                  Parts Inventory
                </h2>
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #e5e7eb",
                    boxShadow: "var(--shadow)",
                    overflowX: "auto",
                    width: "100%",
                  }}
                >
                  <div
                    className="d-flex flex-wrap align-items-center mb-3"
                    style={{ gap: "0.5rem" }}
                  >
                    <span
                      style={{
                        backgroundColor: "#ef4444",
                        color: "white",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      12
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        marginRight: "0.5rem",
                      }}
                    >
                      Critical Stock
                    </span>
                    <span
                      style={{
                        backgroundColor: "#f59e42",
                        color: "white",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      4
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        marginRight: "0.5rem",
                      }}
                    >
                      Low Stock
                    </span>
                    <span
                      style={{
                        backgroundColor: "#22c55e",
                        color: "white",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      23
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        marginRight: "0.5rem",
                      }}
                    >
                      Total Items
                    </span>
                    <span
                      style={{
                        backgroundColor: "#22c55e",
                        color: "white",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      AED 27,450
                    </span>
                    <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                      Inventory Value
                    </span>
                  </div>
                  <table
                    style={{
                      width: "100%",
                      fontSize: "0.875rem",
                      borderCollapse: "collapse",
                    }}
                  >
                    <thead className="table-header-primary">
                      <tr>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          Part Info
                        </th>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          Category
                        </th>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          Stock Status
                        </th>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          Price & Value
                        </th>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          Supplier
                        </th>
                        <th style={{ padding: "0.5rem", textAlign: "left" }}>
                          Location
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          part: "Engine Oil Filter, ID: 12345, Brand: AutoParts",
                          category: "Engine",
                          status: "Good (12/50)",
                          statusColor: "#22c55e",
                          price: "AED 180 | Total: AED 2,160",
                          supplier: "Auto Parts Inc., Last updated: 2023-11-15",
                          location: "B2-08",
                        },
                        {
                          part: "Brake Pads, ID: 67890, Brand: BrakeMaster",
                          category: "Brakes",
                          status: "Critical (2/20) Reorder now!",
                          statusColor: "#ef4444",
                          price: "AED 90 | Total: AED 180",
                          supplier: "BrakeMaster, Last updated: 2023-11-10",
                          location: "A1-05",
                        },
                        {
                          part: "Spark Plugs, ID: 24680, Brand: Sparky",
                          category: "Engine",
                          status: "Good (25/100)",
                          statusColor: "#22c55e",
                          price: "AED 20 | Total: AED 500",
                          supplier: "Auto Parts Inc., Last updated: 2023-11-20",
                          location: "C3-12",
                        },
                        {
                          part: "Tires, ID: 13579, Brand: TireCo",
                          category: "Wheels",
                          status: "Good (4/16)",
                          statusColor: "#22c55e",
                          price: "AED 300 | Total: AED 1,200",
                          supplier: "TireCo, Last updated: 2023-11-05",
                          location: "D4-15",
                        },
                        {
                          part: "Headlights, ID: 97531, Brand: LightUp",
                          category: "Exterior",
                          status: "Critical (1/10) Reorder now!",
                          statusColor: "#ef4444",
                          price: "AED 150 | Total: AED 150",
                          supplier: "LightUp, Last updated: 2023-11-18",
                          location: "E5-20",
                        },
                      ].map((row, index) => (
                        <tr
                          key={index}
                          style={{
                            borderBottom:
                              index < 4 ? "1px solid #f3f4f6" : "none",
                          }}
                        >
                          <td
                            style={{ padding: "0.5rem", verticalAlign: "top" }}
                          >
                            {row.part}
                          </td>
                          <td
                            style={{ padding: "0.5rem", verticalAlign: "top" }}
                          >
                            <span
                              style={{
                                backgroundColor: "#f3f4f6",
                                color: "#1f2937",
                                padding: "0.125rem 0.375rem",
                                borderRadius: "0.25rem",
                                fontSize: "0.75rem",
                              }}
                            >
                              {row.category}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "0.5rem",
                              verticalAlign: "top",
                              color: row.statusColor,
                            }}
                          >
                            {row.status}
                          </td>
                          <td
                            style={{ padding: "0.5rem", verticalAlign: "top" }}
                          >
                            {row.price}
                          </td>
                          <td
                            style={{ padding: "0.5rem", verticalAlign: "top" }}
                          >
                            {row.supplier}
                          </td>
                          <td
                            style={{ padding: "0.5rem", verticalAlign: "top" }}
                          >
                            <span
                              style={{
                                backgroundColor: "#6b7280",
                                color: "white",
                                padding: "0.125rem 0.375rem",
                                borderRadius: "0.25rem",
                                fontSize: "0.75rem",
                              }}
                            >
                              {row.location}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Services Tab Content */}
          {activeTab === "services" && (
            <div className="row max-h-65vh p-3" style={{ gap: "0.75rem" }}>
              <div className="col-md-4">
                <div className="d-flex flex-column" style={{ gap: "0.75rem" }}>
                  {[
                    {
                      label: "Services Completed",
                      value: "98",
                      subtitle: "This Week",
                    },
                    {
                      label: "Services Pending",
                      value: "98",
                      subtitle: "Active Queue",
                    },
                    {
                      label: "Avg Service Time",
                      value: "1.5h",
                      subtitle: "Per Vehicle",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="d-flex flex-column border-left-primary"
                      style={{
                        backgroundColor: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        borderRadius: "0.75rem",
                        paddingLeft: "0.75rem",
                        paddingRight: "1.25rem",
                        paddingTop: "0.75rem",
                        paddingBottom: "0.75rem",
                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span
                          style={{ color: "#6b7280", fontSize: "0.875rem" }}
                        >
                          {item.label}
                        </span>
                        <span
                          style={{
                            fontSize: "1.5rem",
                            fontWeight: "bold",
                            color: "#1f2937",
                          }}
                        >
                          {item.value}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: "0.875rem",
                          color: "#6b7280",
                          marginTop: "0.25rem",
                        }}
                      >
                        {item.subtitle}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-md-8">
                <h2
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                  }}
                >
                  Weekly Service Performance
                </h2>
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #e5e7eb",
                    boxShadow: "var(--shadow)",
                  }}
                >
                  <canvas
                    ref={serviceChartRef}
                    style={{ width: "100%", height: "12rem" }}
                  ></canvas>
                </div>
                <div
                  className="d-flex justify-content-end mt-3 align-items-center"
                  style={{ fontSize: "0.875rem", gap: "1rem" }}
                >
                  <span className="d-flex align-items-center">
                    <span
                      className="d-inline-block rounded-circle me-2"
                      style={{
                        width: "0.75rem",
                        height: "0.75rem",
                        backgroundColor: "#f3f4f6",
                      }}
                    ></span>
                    Pending
                  </span>
                  <span className="d-flex align-items-center">
                    <span
                      className="d-inline-block rounded-circle me-2"
                      style={{
                        width: "0.75rem",
                        height: "0.75rem",
                        backgroundColor: "#22c55e",
                      }}
                    ></span>
                    Completed
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebView;

